import React, { useState, useMemo } from 'react';
import { FaDownload, FaExpand, FaCompress, FaTimes } from 'react-icons/fa';
import './TableRenderer.css';

const TableRenderer = ({ tableData, title = "Table" }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('csv');

  // Parse markdown table data
  const parsedTable = useMemo(() => {
    if (!tableData) return null;
    
    // Split by lines and filter out empty lines
    const lines = tableData.split('\n').filter(line => line.trim());
    
    // Find table start and end
    let tableStart = -1;
    let tableEnd = -1;
    
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes('|') && lines[i].trim().startsWith('|')) {
        if (tableStart === -1) tableStart = i;
        tableEnd = i;
      }
    }
    
    if (tableStart === -1) return null;
    
    // Extract table lines
    const tableLines = lines.slice(tableStart, tableEnd + 1);
    
    // Parse headers
    const headerLine = tableLines[0];
    const headers = headerLine.split('|')
      .map(cell => cell.trim())
      .filter(cell => cell && cell !== '');
    
    // Parse separator line (skip it)
    const dataLines = tableLines.slice(2);
    
    // Parse data rows
    const rows = dataLines.map(line => {
      const cells = line.split('|')
        .map(cell => cell.trim())
        .filter(cell => cell && cell !== '');
      return cells;
    });
    
    return { headers, rows };
  }, [tableData]);

  if (!parsedTable) {
    return <div className="table-error">Invalid table format</div>;
  }

  const { headers, rows } = parsedTable;

  // Convert to different formats
  const convertToCSV = () => {
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    return csvContent;
  };

  const convertToJSON = () => {
    const jsonData = rows.map(row => {
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = row[index] || '';
      });
      return obj;
    });
    return JSON.stringify(jsonData, null, 2);
  };

  const convertToMarkdown = () => {
    const separator = headers.map(() => '---').join('|');
    const headerRow = headers.join('|');
    const dataRows = rows.map(row => row.join('|'));
    
    return [
      `|${headerRow}|`,
      `|${separator}|`,
      ...dataRows.map(row => `|${row}|`)
    ].join('\n');
  };

  const downloadTable = () => {
    let content, filename, mimeType;
    
    switch (selectedFormat) {
      case 'csv':
        content = convertToCSV();
        filename = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
        mimeType = 'text/csv';
        break;
      case 'json':
        content = convertToJSON();
        filename = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
        mimeType = 'application/json';
        break;
      case 'markdown':
        content = convertToMarkdown();
        filename = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.md`;
        mimeType = 'text/markdown';
        break;
      default:
        return;
    }
    
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const TableContent = ({ showTitle = true }) => (
    <div className="table-container">
      <div className={`table-header ${!showTitle ? 'no-title' : ''}`}>
        {showTitle && <h3 className="table-title">{title}</h3>}
        <div className="table-controls">
          <select 
            value={selectedFormat} 
            onChange={(e) => setSelectedFormat(e.target.value)}
            className="format-selector"
          >
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
            <option value="markdown">Markdown</option>
          </select>
          <button 
            onClick={downloadTable} 
            className="download-btn"
            title="Download table"
          >
            <FaDownload />
          </button>
          <button 
            onClick={toggleFullscreen} 
            className="fullscreen-btn"
            title={isFullscreen ? "Exit fullscreen" : "View fullscreen"}
          >
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
      </div>
      
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={index} className="table-header-cell">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="table-row">
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="table-cell">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (isFullscreen) {
    return (
      <div className="fullscreen-modal">
        <div className="fullscreen-content">
          <div className="fullscreen-header">
            <h2 className="fullscreen-title">{title}</h2>
            <button 
              onClick={toggleFullscreen} 
              className="close-fullscreen-btn"
              title="Close fullscreen"
            >
              <FaTimes />
            </button>
          </div>
          <div className="fullscreen-table-container">
            <TableContent showTitle={false} />
          </div>
        </div>
      </div>
    );
  }

  return <TableContent />;
};

export default TableRenderer;
